// Fichas de Google por confirmar (Fase 2, cierre de pendientes de la Fase 1): junta en una
// sola pantalla todas las empresas donde "Analizar reseñas" encontró una ficha de Google que no
// coincidía sola, para no tener que revisarlas de una en una desde la ficha de cada empresa.
// Confirmar "Sí es" solo guarda la vinculación (reviews_confirm_link): bajar las reseñas es un
// paso aparte, con su propio aviso de cuántas búsquedas gasta (ReviewsButton, ya existente).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { box } from "../ficha/FichaTabs";
import { MobilePage } from "../MobilePage";
import { LinkForm, ReviewsButton } from "./reviews";
import type { Place, PlaceCheck } from "./reviews";

const sb = () => getSupabaseClient();

type PendingRow = {
  directory_id: number;
  nombre_comercial: string | null;
  direccion: string | null;
  telefono: string | null;
  municipio: string | null;
  estado: string | null;
  thermo_base: number | null;
  company_id: number | null;
  candidate: (Place & { check: PlaceCheck }) | null;
  max_reviews: number;
  status: string;
  data_id: string | null;
};

export const usePendingConfirm = (enabled = true) =>
  useQuery({
    queryKey: ["reviews_pending_confirm"],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await sb()
        .from("reviews_pending_confirm")
        .select(
          "directory_id, nombre_comercial, direccion, telefono, municipio, estado, thermo_base, company_id, candidate, max_reviews, status, data_id",
        );
      if (error) throw error;
      return (data ?? []) as PendingRow[];
    },
  });

const Yes = ({ v }: { v: boolean | null | undefined }) =>
  v == null ? (
    <span className="text-muted-foreground">sin dato</span>
  ) : v ? (
    <b className="text-[var(--good)]">coincide</b>
  ) : (
    <b className="text-[var(--critical)]">no coincide</b>
  );

/** Una empresa con ficha candidata: Sí es / No es / pegar enlace. */
const ConfirmRow = ({ r }: { r: PendingRow }) => {
  const c = r.candidate!;
  const notify = useNotify();
  const qc = useQueryClient();
  const act = async (ok: boolean) => {
    const { error } = await sb().rpc("reviews_confirm_link", {
      p_dir: r.directory_id,
      p_ok: ok,
    });
    if (error) return notify(error.message, { type: "error" });
    notify(
      ok ? "Vinculada: falta bajar sus reseñas" : "Marcada como no encontrada",
      {
        type: "success",
      },
    );
    qc.invalidateQueries({ queryKey: ["reviews_pending_confirm"] });
  };
  return (
    <div className={cn(box, "flex flex-col gap-2 text-sm")}>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-md border p-2">
          <div className="text-xs text-muted-foreground">Tu directorio</div>
          <b>{r.nombre_comercial}</b>
          <div>{r.direccion ?? "sin dirección"}</div>
          <div className="text-xs text-muted-foreground">
            Tel. {r.telefono ?? "sin teléfono"} · {r.municipio}, {r.estado}
          </div>
        </div>
        <div className="rounded-md border p-2">
          <div className="text-xs text-muted-foreground">
            <MapPin className="inline size-3.5" /> Ficha de Google
          </div>
          <b>{c.name}</b>
          <div>{c.address ?? "sin dirección"}</div>
          <div className="text-xs text-muted-foreground">
            {c.total ?? 0} calificaciones · {c.rating ?? "—"} ★
            {c.check?.distancia_m != null ? ` · ${c.check.distancia_m} m` : ""}
          </div>
        </div>
      </div>
      <div className="text-xs">
        Nombre: {Math.round((c.check?.nombre ?? 0) * 100)} % de sus palabras ·
        C.P.: <Yes v={c.check?.cp} /> · Teléfono: <Yes v={c.check?.tel} /> ·
        Municipio: <Yes v={c.check?.municipio} />
        {c.check?.score != null ? ` · puntaje ${c.check.score}` : ""}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => act(true)}>
          Sí, es esta
        </Button>
        <Button size="sm" variant="outline" onClick={() => act(false)}>
          No es esta
        </Button>
      </div>
      <span className="text-xs text-muted-foreground">
        ¿Tienes la correcta? Pega su enlace:
      </span>
      <LinkForm dirId={r.directory_id} />
    </div>
  );
};

/** Ya vinculada: falta bajar sus reseñas (paso aparte, con aviso de cuánto gasta). */
const LinkedRow = ({ r }: { r: PendingRow }) => (
  <div className={cn(box, "flex flex-col gap-2 text-sm")}>
    <div>
      <b>{r.nombre_comercial}</b>{" "}
      <span className="text-xs text-muted-foreground">
        · {r.municipio}, {r.estado}
      </span>
    </div>
    <span className="text-xs text-muted-foreground">
      Ficha confirmada, todavía sin bajar sus reseñas.
    </span>
    <div>
      <ReviewsButton ids={[r.directory_id]} label="Bajar sus reseñas" />
    </div>
  </div>
);

export const GoogleConfirmPage = () => {
  const { data, isLoading } = usePendingConfirm();
  const porConfirmar = (data ?? []).filter((r) => r.candidate);
  const vinculadas = (data ?? []).filter(
    (r) => !r.candidate && r.status === "vinculada",
  );
  return (
    <MobilePage title="Fichas de Google por confirmar">
      <div className="mx-auto flex max-w-3xl flex-col gap-4 p-4">
        <div>
          <h1 className="text-xl font-bold">Fichas de Google por confirmar</h1>
          <p className="text-sm text-muted-foreground">
            Empresas donde "Analizar reseñas" encontró una ficha en Google que
            no coincidió sola. Confirmar aquí no gasta nada: bajar las reseñas
            es un botón aparte, con aviso de cuántas búsquedas usa.
          </p>
        </div>
        {isLoading && (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        )}
        {!isLoading && !porConfirmar.length && !vinculadas.length && (
          <p className="text-sm text-muted-foreground">
            Sin fichas pendientes por ahora.
          </p>
        )}
        {porConfirmar.length > 0 && (
          <div className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-muted-foreground">
              Por confirmar ({porConfirmar.length})
            </h2>
            {porConfirmar.map((r) => (
              <ConfirmRow key={r.directory_id} r={r} />
            ))}
          </div>
        )}
        {vinculadas.length > 0 && (
          <div className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-muted-foreground">
              Confirmadas, sin bajar reseñas ({vinculadas.length})
            </h2>
            {vinculadas.map((r) => (
              <LinkedRow key={r.directory_id} r={r} />
            ))}
          </div>
        )}
      </div>
    </MobilePage>
  );
};
